import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import * as FormDialog from "../../../shared/FormDialog";
import { useUpdateRegistrationStatus } from "../../../api/registrations";
import type { RegistrationStatus } from "../../../api/registrations.types";

type StatusChangeRequest = {
  status: RegistrationStatus["status"];
  auditStatus?: string;
  additionalText: string;
};

type FormValues = {
  additionalText: string;
};

type RegistrationRequestCompletionDialogProps = {
  onClose: () => void;
  registrationId: string;
  status: RegistrationStatus["status"];
};

export const RegistrationRequestCompletionDialog: FC<RegistrationRequestCompletionDialogProps> = ({
  onClose,
  registrationId,
  status,
}) => {
  const { t } = useTranslation();
  const updateStatusMutation = useUpdateRegistrationStatus(registrationId);
  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: { additionalText: "" },
  });

  const handleConfirm = ({ additionalText }: FormValues) => {
    const statusIsAudit = status === "audit";
    const payload: StatusChangeRequest = statusIsAudit
      ? { status: "audit", auditStatus: "completion", additionalText }
      : { status: "completion", additionalText };

    return updateStatusMutation.mutateAsync(payload, { onSuccess: onClose });
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("RegistrationDrawer.RequestCompletion.DialogHeader")}</FormDialog.Title>
      <FormDialog.Form onSubmit={handleSubmit(handleConfirm)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={updateStatusMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="additionalText"
              rules={{ required: t("Input.Validation.Required") }}
              label={t("RegistrationDrawer.RequestCompletion.Label")}
              multiline
              rows={7}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateStatusMutation.isPending}>
            {t("RegistrationDrawer.RequestCompletion.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
